// src/utils/phoneNumberFormatter.js

// Country data with dial codes and validation rules
const COUNTRIES = [
  { code: 'US', dialCode: '+1', digitCount: 10 },
  { code: 'GB', dialCode: '+44', digitCount: 10 },
  { code: 'CA', dialCode: '+1', digitCount: 10 },
  { code: 'AU', dialCode: '+61', digitCount: 9 },
  { code: 'IN', dialCode: '+91', digitCount: 10 },
  { code: 'PK', dialCode: '+92', digitCount: 10 },
  { code: 'BD', dialCode: '+880', digitCount: 10 },
  { code: 'LK', dialCode: '+94', digitCount: 9 },
  { code: 'AE', dialCode: '+971', digitCount: 9 },
  { code: 'SA', dialCode: '+966', digitCount: 9 },
  { code: 'CN', dialCode: '+86', digitCount: 11 },
  { code: 'JP', dialCode: '+81', digitCount: 10 },
  { code: 'SG', dialCode: '+65', digitCount: 8 },
  { code: 'MY', dialCode: '+60', digitCount: 9 },
  { code: 'TH', dialCode: '+66', digitCount: 9 },
  { code: 'PH', dialCode: '+63', digitCount: 10 },
  { code: 'ID', dialCode: '+62', digitCount: 10 },
  { code: 'VN', dialCode: '+84', digitCount: 9 },
  { code: 'DE', dialCode: '+49', digitCount: 10 },
  { code: 'FR', dialCode: '+33', digitCount: 9 },
  { code: 'IT', dialCode: '+39', digitCount: 10 },
  { code: 'ES', dialCode: '+34', digitCount: 9 },
  { code: 'BR', dialCode: '+55', digitCount: 11 },
  { code: 'MX', dialCode: '+52', digitCount: 10 },
  { code: 'ZA', dialCode: '+27', digitCount: 9 },
  { code: 'NG', dialCode: '+234', digitCount: 10 },
  { code: 'EG', dialCode: '+20', digitCount: 10 },
  { code: 'KE', dialCode: '+254', digitCount: 9 },
];

/**
 * Split a raw phone field value into individual phone number strings.
 * Handles comma/semicolon, whitespace, '+' in middle, and concatenated digits.
 *
 * @param {string} rawInput - Raw value from the Phone_1 (or any) Excel cell
 * @param {string} defaultCountryCode - Business country code (e.g., 'LK')
 * @returns {string[]} - Array of 1 or 2 phone string tokens
 */
export const splitRawPhoneInput = (rawInput, defaultCountryCode = 'LK') => {
  if (!rawInput) return [];

  let input = rawInput.toString().trim();

  // Strip common text prefixes like "p:", "tel:", "mobile:" etc.
  input = input.replace(/^[a-z]+:/i, '').trim();

  if (!input) return [];

  // 1. Comma or semicolon split
  if (/[,;]/.test(input)) {
    return input.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
  }

  // 2. Whitespace split (only when there are 2+ tokens)
  const spaceParts = input.split(/\s+/).filter(Boolean);
  if (spaceParts.length >= 2) {
    return spaceParts;
  }

  // 3. '+' appearing in the middle of the string (not at position 0)
  //    e.g. "0782848268+94778520518" or "+94782848268+94778520518"
  let plusMidPos = -1;
  for (let i = 1; i < input.length; i++) {
    if (input[i] === '+') { plusMidPos = i; break; }
  }
  if (plusMidPos !== -1) {
    const first  = input.substring(0, plusMidPos).trim();
    const second = input.substring(plusMidPos).trim();
    if (first && second) return [first, second];
  }

  // 4. Pure-digit concatenation (no separators, no '+')
  //    e.g. "782848268778520518" → two 9-digit LK numbers
  const digitsOnly = input.replace(/\D/g, '');
  if (digitsOnly.length === input.length) {
    const country = COUNTRIES.find(c => c.code === defaultCountryCode)
      || COUNTRIES.find(c => c.code === 'LK');
    const n = country.digitCount; // e.g. 9 for LK

    // Try expected length combinations: (n,n), (n+1,n), (n,n+1), (n+1,n+1)
    // where n+1 accounts for a leading local-dial '0'
    for (const [len1, len2] of [[n, n], [n + 1, n], [n, n + 1], [n + 1, n + 1]]) {
      if (digitsOnly.length === len1 + len2) {
        return [digitsOnly.substring(0, len1), digitsOnly.substring(len1)];
      }
    }
  }

  return [input];
};

/**
 * Format and validate a phone number
 * @param {string} phoneNumber - Raw phone number from Excel
 * @param {string} defaultCountryCode - Business country code (e.g., 'LK')
 * @returns {object} - { isValid, formattedNumber, error, detectedCountry }
 */
export const formatPhoneNumber = (phoneNumber, defaultCountryCode = 'LK') => {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return {
      isValid: false,
      formattedNumber: null,
      error: 'Phone number is required',
      detectedCountry: null
    };
  }

  let cleanNumber = phoneNumber.trim();

  // Strip common text prefixes like "p:", "tel:", "mobile:" etc.
  cleanNumber = cleanNumber.replace(/^[a-z]+:/i, '').trim();

  // Remove all whitespace and formatting characters except '+'
  cleanNumber = cleanNumber.replace(/[\s\-\(\)]/g, '');

  let detectedCountry = null;
  let numberWithoutCode = cleanNumber;

  if (cleanNumber.startsWith('+')) {
    // Has explicit '+' prefix — match against known dial codes
    for (const country of COUNTRIES) {
      if (cleanNumber.startsWith(country.dialCode)) {
        detectedCountry = country;
        numberWithoutCode = cleanNumber.substring(country.dialCode.length);
        break;
      }
    }

    if (!detectedCountry) {
      // Unknown or unrecognised country code — strip it and fall back to the
      // default country.  Strategy: take the last digitCount digits after '+'
      // as the local number (e.g. "+93778520518" → last 9 digits "778520518"
      // → "+94778520518" for LK).
      const defaultCountry = COUNTRIES.find(c => c.code === defaultCountryCode)
        || COUNTRIES.find(c => c.code === 'LK');
      const digitsAfterPlus = cleanNumber.substring(1).replace(/\D/g, '');
      const n = defaultCountry.digitCount;

      if (digitsAfterPlus.length >= n) {
        const localDigits = digitsAfterPlus.slice(-n);
        return {
          isValid: true,
          formattedNumber: `${defaultCountry.dialCode}${localDigits}`,
          error: null,
          detectedCountry: defaultCountry.code
        };
      }

      return {
        isValid: false,
        formattedNumber: null,
        error: `Phone number too short or has an invalid country code: ${cleanNumber}`,
        detectedCountry: null
      };
    }
  } else {
    // No '+' prefix — check if the number starts with a known dial code (digits only)
    // e.g. "94771234567" should match dial code "94" for LK
    const digitsOnly = cleanNumber.replace(/\D/g, '');
    let matchedByDialCode = false;

    // Sort by dial code length descending to match longest prefix first
    const sortedCountries = [...COUNTRIES].sort(
      (a, b) => b.dialCode.length - a.dialCode.length
    );

    for (const country of sortedCountries) {
      const dialDigits = country.dialCode.replace('+', '');
      if (digitsOnly.startsWith(dialDigits)) {
        const remaining = digitsOnly.substring(dialDigits.length);
        // Strip a leading '0' that may appear after the dial code (e.g. +94 077...)
        const remainingStripped = remaining.replace(/^0+/, '');

        // Only accept this match if the remaining digits match the expected count
        if (remainingStripped.length === country.digitCount) {
          detectedCountry = country;
          numberWithoutCode = remaining; // keep for stripping below
          matchedByDialCode = true;
          break;
        }
      }
    }

    if (!matchedByDialCode) {
      // Fall back to default country — treat as local number
      detectedCountry = COUNTRIES.find(c => c.code === defaultCountryCode)
        || COUNTRIES.find(c => c.code === 'LK');
      numberWithoutCode = cleanNumber.replace(/\+/g, '');
    }
  }

  // Extract digits and strip leading zeros (e.g. 077... → 77...)
  let digits = numberWithoutCode.replace(/\D/g, '').replace(/^0+/, '');

  if (!digits) {
    return {
      isValid: false,
      formattedNumber: null,
      error: 'Phone number contains no digits',
      detectedCountry: null
    };
  }

  if (digits.length < detectedCountry.digitCount) {
    return {
      isValid: false,
      formattedNumber: null,
      error: `Phone number too short for ${detectedCountry.code}. Expected ${detectedCountry.digitCount} digits, got ${digits.length}`,
      detectedCountry: detectedCountry.code
    };
  }

  if (digits.length > detectedCountry.digitCount) {
    return {
      isValid: false,
      formattedNumber: null,
      error: `Phone number too long for ${detectedCountry.code}. Expected ${detectedCountry.digitCount} digits, got ${digits.length}`,
      detectedCountry: detectedCountry.code
    };
  }

  return {
    isValid: true,
    formattedNumber: `${detectedCountry.dialCode}${digits}`,
    error: null,
    detectedCountry: detectedCountry.code
  };
};

/**
 * Format multiple phone numbers
 * @param {string[]} phoneNumbers - Array of raw phone numbers
 * @param {string} defaultCountryCode - Business country code
 * @returns {object} - { isValid, formattedNumbers, errors }
 */
export const formatPhoneNumbers = (phoneNumbers, defaultCountryCode = 'LK') => {
  const results = phoneNumbers.map(phone => formatPhoneNumber(phone, defaultCountryCode));
  
  const formattedNumbers = results
    .filter(r => r.isValid)
    .map(r => r.formattedNumber);
  
  const errors = results
    .filter(r => !r.isValid)
    .map(r => r.error);
  
  return {
    isValid: errors.length === 0,
    formattedNumbers,
    errors
  };
};

/**
 * Get country by country code
 * @param {string} countryCode - ISO country code (e.g., 'LK')
 * @returns {object|null} - Country object or null
 */
export const getCountryByCode = (countryCode) => {
  return COUNTRIES.find(c => c.code === countryCode) || null;
};